import { AlertTriangle, KeyRound, LineChart, Sparkles, Zap } from 'lucide-react'
import { useEffect, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Logo } from '@/components/layout/logo.tsx'
import { Button } from '@/components/ui/button.tsx'
import { DecorativeBlobs } from '@/components/ui/decor.tsx'
import { signInWithGoogle, useSession } from '@/lib/auth-client.ts'

const FEATURES = [
  {
    icon: <Zap className="size-6" />,
    title: 'Top-up instan',
    description: 'Bayar dengan QRIS dari mobile banking atau e-wallet, saldo masuk otomatis.',
  },
  {
    icon: <KeyRound className="size-6" />,
    title: 'API key aman',
    description: 'Buat, batasi, dan cabut API key kapan saja untuk tiap aplikasimu.',
  },
  {
    icon: <LineChart className="size-6" />,
    title: 'Pemakaian transparan',
    description: 'Lihat setiap request, token, durasi, dan biayanya sampai enam desimal.',
  },
]

/**
 * Better Auth appends the reason to the error redirect. Without this the user
 * just lands back on the sign-in page with no explanation of what failed.
 */
const SIGN_IN_ERRORS: Record<string, string> = {
  state_mismatch:
    'Sesi login tidak cocok. Ini biasanya terjadi kalau cookie diblokir browser — coba lagi, atau buka lewat domain utama.',
  invalid_state: 'Sesi login sudah kedaluwarsa. Silakan coba lagi.',
  access_denied: 'Kamu membatalkan izin di halaman Google.',
  please_restart_the_process: 'Proses login terputus. Silakan mulai lagi.',
}

export function LandingPage() {
  const session = useSession()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const errorCode = searchParams.get('error')
  const errorMessage = errorCode
    ? (SIGN_IN_ERRORS[errorCode] ?? `Login gagal (${errorCode}). Silakan coba lagi.`)
    : null

  useEffect(() => {
    if (session.data?.user) navigate('/dashboard', { replace: true })
  }, [session.data, navigate])

  return (
    <div className="min-h-screen bg-surface">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-outline-variant bg-surface/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4">
          <Logo />
          <GoogleButton size="sm" />
        </div>
      </header>

      <main className="px-4 pb-20 pt-28">
        <section className="relative mx-auto max-w-5xl overflow-hidden rounded-2xl bg-surface-container px-6 py-16 shadow-level-3 md:rounded-3xl md:px-12 md:py-24">
          <DecorativeBlobs />

          <div className="relative mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-container px-4 py-2 text-label-md font-medium text-on-secondary-container">
              <Sparkles className="size-4" aria-hidden="true" />
              Platform AI credit
            </span>

            <h1 className="mt-8 text-headline-md font-medium leading-tight text-on-surface md:text-display-lg">
              Kredit AI untuk aplikasimu,
              <span className="mt-2 block text-primary">bayar pakai QRIS</span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-body-lg text-on-surface-variant">
              Satu saldo untuk semua model. Isi ulang dalam hitungan detik, pantau pemakaian
              sampai ke tiap request.
            </p>

            {errorMessage ? (
              <div
                role="alert"
                className="mx-auto mt-8 flex max-w-md items-start gap-3 rounded-md bg-error-container p-4 text-left text-body-md text-on-error-container"
              >
                <AlertTriangle className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                {errorMessage}
              </div>
            ) : null}

            <div className="mt-10 flex justify-center">
              <GoogleButton size="lg" />
            </div>
          </div>
        </section>

        <section className="mx-auto mt-8 grid max-w-5xl gap-6 md:grid-cols-3">
          {FEATURES.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </section>
      </main>

      <footer className="border-t border-outline-variant py-8">
        <nav
          aria-label="Halaman legal"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
        >
          <Link
            to="/privacy"
            className="rounded-sm text-label-md text-on-surface-variant underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
          >
            Kebijakan Privasi
          </Link>
          <Link
            to="/terms"
            className="rounded-sm text-label-md text-on-surface-variant underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
          >
            Syarat &amp; Ketentuan
          </Link>
        </nav>
        <p className="mt-4 text-center text-label-sm text-on-surface-variant">
          © {new Date().getFullYear()} YT Clip AI
        </p>
      </footer>
    </div>
  )
}

function GoogleButton({ size }: { size: 'sm' | 'lg' }) {
  return (
    <Button size={size} onClick={() => void signInWithGoogle('/dashboard')}>
      Masuk dengan Google
    </Button>
  )
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: ReactNode
  title: string
  description: string
}) {
  return (
    <div className="group rounded-lg bg-surface-container p-6 shadow-level-1 transition-all duration-300 ease-emphasized hover:scale-[1.02] hover:shadow-level-2">
      <span className="inline-flex size-12 items-center justify-center rounded-sm bg-primary-container text-on-primary-container">
        {icon}
      </span>
      <h2 className="mt-4 text-title-lg font-medium text-on-surface">{title}</h2>
      <p className="mt-2 text-body-md text-on-surface-variant">{description}</p>
    </div>
  )
}
